// Verification Targets
// ---------------------
// Builds the ownership-verification instructions shown to the user: the web-app
// META TAG (on the page at the entered URL) plus, for each configured API
// backend, an HTTP nimis-verify endpoint to serve. Mirrors the backend's
// WebApplication verify logic so what we show is exactly what the backend
// checks — but the backend is AUTHORITATIVE; this is display-only and the
// frontend never performs the check itself. DNS verification is removed.
//
// Shared by the Add-Application wizard's Verify step and the standalone Verify
// modal so the two never drift.

import { NIMIS_VERIFY_PATH_SEGMENT, VERIFICATION_META_TAG_NAME } from "@/constants/definitions";
import { canonicalApiDomain, parseApiDomainEntry, toApiDomainList } from "@/utils/apiDomains";

export type VerificationMethod = "meta-tag" | "nimis-verify";

export interface VerificationTarget {
  // Unique display label + React key: the web-app host, or the API "host/path".
  label: string;
  // True for the web app (meta tag); false for a configured API backend.
  isPrimary: boolean;
  method: VerificationMethod;
  // The single value the user copies to their site:
  //   meta-tag     -> the full <meta name="nimis-verify" content="<token>"> tag
  //   nimis-verify -> the endpoint URL to create (serve the token as text/plain)
  copyValue: string;
  // The verification token (the same value for every target).
  token: string;
}

// Derive the hostname from a bare domain, subdomain, host:port, or full URL.
// Returns "" when no host can be derived. Parsing lives in [apiDomains.ts] so
// this surface and the Pentest Scope preview never disagree on what a host is.
export function hostFromEntry(entry: string): string {
  const parsed = parseApiDomainEntry(entry);
  return parsed ? parsed.host : "";
}

function metaTag(token: string): string {
  return `<meta name="${VERIFICATION_META_TAG_NAME}" content="${token}">`;
}

function nimisVerifyUrl(host: string, basePath: string): string {
  return `https://${host}${basePath}/${NIMIS_VERIFY_PATH_SEGMENT}`;
}

// Build the ordered list of verification targets: the web-app meta tag first,
// then one nimis-verify endpoint per configured API domain, de-duplicated by
// canonical "host/path" (case-insensitive). Entries that don't resolve to a
// host are skipped. Returns [] when the URL yields no host — callers should
// treat that as "nothing to show yet".
export function getVerificationTargets(
  url: string | undefined,
  apiDomains: unknown,
  verificationString: string,
): VerificationTarget[] {
  const targets: VerificationTarget[] = [];
  const token = verificationString || "";

  const primaryHost = hostFromEntry(url || "");
  if (primaryHost) {
    targets.push({
      label: primaryHost,
      isPrimary: true,
      method: "meta-tag",
      copyValue: metaTag(token),
      token,
    });
  }

  const seenEntries = new Set<string>();
  for (const entry of toApiDomainList(apiDomains)) {
    const parsed = parseApiDomainEntry(entry);
    if (!parsed) continue;
    const canonical = canonicalApiDomain(parsed);
    const key = canonical.toLowerCase();
    if (seenEntries.has(key)) continue;
    seenEntries.add(key);
    targets.push({
      label: canonical,
      isPrimary: false,
      method: "nimis-verify",
      copyValue: nimisVerifyUrl(parsed.host, parsed.basePath),
      token,
    });
  }

  return targets;
}
